<template>
  <div class="storage-account-details">
    <div class="flex items-center justify-between mb-6">
      <div>
        <vs-button @click="$router.go(-1)" color="primary" type="border">
          <feather-icon icon="ArrowLeftIcon" svgClasses="w-4 h-4" />
          <span class="ml-2">Back to List</span>
        </vs-button>
      </div>
      <div class="flex space-x-2">
        <vs-button @click="syncAccount" color="success" :loading="syncing">
          <feather-icon icon="RefreshCwIcon" svgClasses="w-4 h-4" />
          <span class="ml-2">Sync Now</span>
        </vs-button>
        <vs-button @click="editAccount" color="warning">
          <feather-icon icon="EditIcon" svgClasses="w-4 h-4" />
          <span class="ml-2">Edit Account</span>
        </vs-button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center items-center h-64">
      <vs-loading color="primary" />
    </div>

    <!-- Account Details -->
    <div v-else-if="account" class="space-y-6">
      <!-- Basic Information -->
      <vx-card title="Account Information">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-grey">Account Title</label>
              <p class="mt-1 text-lg">{{ account.account_title }}</p>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-grey">Account Name</label>
              <p class="mt-1"><code class="bg-primary-light text-primary px-2 py-1 rounded">{{ account.account_name }}</code></p>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-grey">Status</label>
              <vs-chip 
                :color="getStatusColor(account.status)" 
                class="mt-1 font-medium"
              >
                {{ getStatusText(account.status) }}
              </vs-chip>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-grey">Created</label>
              <p class="mt-1">{{ formatDate(account.created_at) }}</p>
            </div>
          </div>
          
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-grey">User</label>
              <div class="mt-1">
                <p class="font-medium">{{ account.user ? `${account.user.first_name} ${account.user.last_name}` : 'N/A' }}</p>
                <p class="text-sm text-grey">{{ account.user ? account.user.email : 'N/A' }}</p>
              </div>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-grey">UUID</label>
              <p class="mt-1 text-sm font-mono">{{ account.uuid }}</p>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-grey">Last Sync</label>
              <p class="mt-1">{{ account.last_sync_at ? formatDate(account.last_sync_at) : 'Never' }}</p>
            </div>
          </div>
        </div>
      </vx-card>

      <!-- Usage Statistics -->
      <vx-card title="Storage Usage">
        <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div class="text-center p-4 bg-primary-light rounded">
            <div class="text-2xl font-bold text-primary">{{ account.quota_gb }}</div>
            <div class="text-sm text-grey">Quota (GB)</div>
          </div>
          <div class="text-center p-4 bg-success-light rounded">
            <div class="text-2xl font-bold text-success">{{ account.used_space_gb || 0 }}</div>
            <div class="text-sm text-grey">Used (GB)</div>
          </div>
          <div class="text-center p-4 bg-warning-light rounded">
            <div class="text-2xl font-bold text-warning">{{ account.containers_count || 0 }}</div>
            <div class="text-sm text-grey">Containers</div>
          </div>
          <div class="text-center p-4 bg-info-light rounded">
            <div class="text-2xl font-bold text-info">{{ account.objects_count || 0 }}</div>
            <div class="text-sm text-grey">Objects</div>
          </div>
        </div>

        <!-- Usage Progress Bar -->
        <div class="mb-4">
          <div class="flex justify-between items-center mb-2">
            <span class="text-sm font-medium">Storage Usage</span>
            <span class="text-sm text-grey">{{ getUsagePercentage(account.used_space_gb, account.quota_gb) }}%</span>
          </div>
          <div class="w-full bg-grey-light rounded-full h-3">
            <div 
              class="h-3 rounded-full transition-all duration-300"
              :class="getUsageColorClass(account.used_space_gb, account.quota_gb)"
              :style="`width: ${getUsagePercentage(account.used_space_gb, account.quota_gb)}%`"
            ></div>
          </div>
        </div>
      </vx-card>

      <!-- S3 Credentials -->
      <vx-card title="S3 Compatible Credentials" v-if="account.status === 1">
        <div class="bg-grey-light p-4 rounded">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-grey mb-1">S3 Username</label>
              <div class="flex items-center space-x-2">
                <code class="bg-white px-3 py-2 rounded border flex-1">{{ account.s3_username }}</code>
                <vs-button 
                  size="small" 
                  color="primary" 
                  type="border"
                  @click="copyToClipboard(account.s3_username)"
                >
                  Copy
                </vs-button>
              </div>
            </div>
            <div>
              <label class="block text-sm font-medium text-grey mb-1">S3 Password</label>
              <div class="flex items-center space-x-2">
                <vs-input 
                  :type="showPassword ? 'text' : 'password'"
                  :value="account.s3_password"
                  readonly
                  class="flex-1"
                />
                <vs-button 
                  size="small" 
                  color="warning" 
                  type="border"
                  @click="showPassword = !showPassword"
                >
                  {{ showPassword ? 'Hide' : 'Show' }}
                </vs-button>
                <vs-button 
                  size="small" 
                  color="primary" 
                  type="border"
                  @click="copyToClipboard(account.s3_password)"
                >
                  Copy
                </vs-button>
              </div>
            </div>
          </div>
          <div class="mt-4">
            <label class="block text-sm font-medium text-grey mb-1">S3 Endpoint</label>
            <div class="flex items-center space-x-2">
              <code class="bg-white px-3 py-2 rounded border flex-1">https://drive.haio.ir/v1/AUTH_{{ account.account_name }}</code>
              <vs-button 
                size="small" 
                color="primary" 
                type="border"
                @click="copyToClipboard(`https://drive.haio.ir/v1/AUTH_${account.account_name}`)"
              >
                Copy
              </vs-button>
            </div>
          </div>
        </div>
      </vx-card>

      <!-- Containers -->
      <vx-card title="Containers">
        <div v-if="containers.length === 0" class="text-center py-8 text-grey">
          No containers found
        </div>
        <vs-table v-else :data="containers">
          <template slot="thead">
            <vs-th>Name</vs-th>
            <vs-th>Objects</vs-th>
            <vs-th>Size (Bytes)</vs-th>
            <vs-th>Created</vs-th>
            <vs-th>Last Sync</vs-th>
          </template>

          <template slot-scope="{ data }">
            <vs-tr :data="tr" :key="indextr" v-for="(tr, indextr) in data">
              <vs-td>
                <code class="bg-primary-light text-primary px-2 py-1 rounded">{{ tr.name }}</code>
              </vs-td>
              <vs-td>{{ tr.objects_count || 0 }}</vs-td>
              <vs-td>{{ formatBytes(tr.size_bytes || 0) }}</vs-td>
              <vs-td>{{ formatDate(tr.created_at) }}</vs-td>
              <vs-td>{{ tr.last_sync_at ? formatDate(tr.last_sync_at) : 'Never' }}</vs-td>
            </vs-tr>
          </template>
        </vs-table>
      </vx-card>

      <!-- Activity Logs -->
      <vx-card title="Recent Activity">
        <div v-if="logs.length === 0" class="text-center py-8 text-grey">
          No activity logs found
        </div>
        <div v-else class="space-y-3">
          <div 
            v-for="log in logs" 
            :key="log.id"
            class="flex items-center justify-between p-3 bg-grey-light rounded"
          >
            <div>
              <div class="font-medium">{{ log.action }}</div>
              <div class="text-sm text-grey">{{ log.details || 'No details available' }}</div>
            </div>
            <div class="text-sm text-grey">
              {{ formatDate(log.created_at) }}
            </div>
          </div>
        </div>
      </vx-card>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-8">
      <div class="text-danger text-lg mb-2">Error loading account</div>
      <div class="text-grey">{{ error }}</div>
      <vs-button @click="loadAccount" color="primary" class="mt-4">
        Try Again
      </vs-button>
    </div>

    <!-- Edit Account Modal -->
    <EditAccountModal
      v-if="showEditModal"
      :account="account"
      @close="showEditModal = false"
      @updated="onAccountUpdated"
    />
  </div>
</template>

<script>
import EditAccountModal from './components/EditAccountModal.vue'

export default {
  name: 'StorageAccountDetails',
  components: {
    EditAccountModal
  },
  data() {
    return {
      account: null,
      containers: [],
      logs: [],
      loading: true,
      syncing: false,
      error: null,
      showPassword: false,
      showEditModal: false
    }
  },
  mounted() {
    this.loadAccount()
  },
  methods: {
    async loadAccount() {
      this.loading = true
      this.error = null

      try {
        const accountId = this.$route.params.id
        const response = await this.$http.get(`/admin/haio-smart-storage/${accountId}`)

        if (response.data.status) {
          this.account = response.data.params
          this.containers = this.account.containers || []
          this.logs = this.account.logs || []
        }
      } catch (error) {
        console.error('Error loading account:', error)
        this.error = 'Failed to load account details'
      } finally {
        this.loading = false
      }
    },

    async syncAccount() {
      this.syncing = true
      try {
        const response = await this.$http.post(`/admin/haio-smart-storage/${this.account.id}/sync`)
        if (response.data.status) {
          this.$vs.notify({
            title: 'Success',
            text: 'Sync request sent successfully',
            color: 'success'
          })
          // Reload account data after a delay
          setTimeout(() => {
            this.loadAccount()
          }, 2000)
        }
      } catch (error) {
        this.$vs.notify({
          title: 'Error',
          text: 'Failed to sync account',
          color: 'danger'
        })
      } finally {
        this.syncing = false
      }
    },

    editAccount() {
      this.showEditModal = true
    },

    onAccountUpdated() {
      this.showEditModal = false
      this.loadAccount()
    },

    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.$vs.notify({
          title: 'Copied',
          text: 'Copied to clipboard',
          color: 'success'
        })
      }).catch(() => {
        this.$vs.notify({
          title: 'Error',
          text: 'Failed to copy to clipboard',
          color: 'danger'
        })
      })
    },

    getStatusColor(status) {
      const colors = {
        0: 'warning',  // Creating
        1: 'success',  // Active
        2: 'danger',   // Suspended
        3: 'danger',   // Failed
        4: 'warning'   // Deleting
      }
      return colors[status] || 'primary'
    },

    getStatusText(status) {
      const texts = {
        0: 'Creating',
        1: 'Active',
        2: 'Suspended',
        3: 'Failed',
        4: 'Deleting'
      }
      return texts[status] || 'Unknown'
    },

    getUsagePercentage(used, quota) {
      return quota > 0 ? Math.min((used / quota) * 100, 100) : 0
    },

    getUsageColorClass(used, quota) {
      const percentage = this.getUsagePercentage(used, quota)
      if (percentage < 60) return 'bg-success'
      if (percentage < 80) return 'bg-warning'
      return 'bg-danger'
    },

    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    formatBytes(bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }
  }
}
</script>

<style scoped>
.storage-account-details {
  padding: 1rem;
}
</style>